import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { FileSpreadsheet, Search, Store } from 'lucide-react';
import ExportButton from '../../components/common/ExportButton';
import FilterSelect from '../../components/common/FilterSelect';
import useDebouncedValue from '../../hooks/useDebouncedValue';
import { dairyOrdersAPI } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import { useStoreContextStore } from '../../store/storeContextStore';
import { downloadExcelWorkbook } from '../../utils/exportExcel';

const todayKey = () => new Date().toISOString().split('T')[0];
const shiftDate = (value, days) => {
  const date = new Date(`${value || todayKey()}T00:00:00`);
  date.setDate(date.getDate() + days);
  return date.toISOString().split('T')[0];
};
const formatDate = (value) => {
  if (!value) return '-';
  return new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};
const SEARCH_DEBOUNCE_MS = 800;
const formatEditInfo = (row) => {
  const timestamp = row?.lastEditedAt || row?.createdAt;
  const editor = row?.lastEditedByName || row?.createdByName;
  const summary = row?.lastEditSummary || 'Initial entry created';
  if (!timestamp || !editor) return '-';
  return `${new Date(timestamp).toLocaleString()} | ${editor} | ${summary}`;
};

const transactionTypeOptions = [
  { value: 'all', label: 'All Transactions' },
  { value: 'inward', label: 'Inward' },
  { value: 'outward', label: 'Outward' },
];
const DAIRY_RECORD_SOURCE = 'darkstore_dairy_order_records';

const DailyDairyOrderReport = () => {
  const [searchParams] = useSearchParams();
  const { hasFeatureAccess, isAdmin } = useAuthStore();
  const selectedStore = useStoreContextStore((state) => state.selectedStore);
  const canViewPage = hasFeatureAccess('dairy_order.report');
  const selectedStoreInfoId = selectedStore?.storeInfoId || selectedStore?.store_info_id || '';
  const selectedVendorId = selectedStore?.vendorId || selectedStore?.vendor_id || '';
  const linkedEntryId = searchParams.get('entryId') || '';
  const linkedDate = searchParams.get('date') || '';
  const rowRefs = useRef(new Map());
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);
  const [transactionType, setTransactionType] = useState('all');
  const [fromDate, setFromDate] = useState(linkedDate || shiftDate(todayKey(), -7));
  const [toDate, setToDate] = useState(linkedDate || todayKey());
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(false);
  const loadReport = useCallback(async () => {
    if (!selectedStoreInfoId) {
      setRows([]);
      setMeta({});
      return;
    }

    setLoading(true);
    try {
      const response = await dairyOrdersAPI.getReport({
        search: debouncedSearch,
        transactionType,
        fromDate,
        toDate,
        storeInfoId: selectedStoreInfoId,
        vendorId: selectedVendorId,
      });
      setRows(response.data?.rows || []);
      setMeta(response.data?.meta || {});
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to load dairy order report');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, fromDate, selectedStoreInfoId, selectedVendorId, toDate, transactionType]);
  const exportSingleRow = (row) => {
    downloadExcelWorkbook({
      fileName: `daily-dairy-entry-${row.entryId || 'export'}.xlsx`,
      sheetName: 'Dairy Entry',
      columns: [
        { header: 'Date', key: 'transactionDate', width: 16 },
        { header: 'Type', key: 'transactionType', width: 12 },
        { header: 'Store', key: 'storeName', width: 20 },
        { header: 'Memo', key: 'memoNumber', width: 18 },
        { header: 'Product', key: 'productName', width: 30 },
        { header: 'SKU', key: 'sku', width: 18 },
        { header: 'Qty', key: 'quantity', width: 10 },
      ],
      rows: [row],
    });
  };
  const handleApproveEdit = async (row) => {
    if (!row?.entryId) return;
    try {
      await dairyOrdersAPI.approveEditFromReport({
        sourceRecordId: row.entryId,
        sourceKind: row.source || DAIRY_RECORD_SOURCE,
      });
      await loadReport();
      toast.success('Edit unlock approved');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to approve edit unlock');
    }
  };

  const handleRejectEdit = async (row) => {
    if (!row?.entryId) return;
    try {
      await dairyOrdersAPI.rejectEditFromReport({
        sourceRecordId: row.entryId,
        sourceKind: row.source || DAIRY_RECORD_SOURCE,
      });
      await loadReport();
      toast.success('Edit unlock rejected');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to reject edit unlock');
    }
  };

  useEffect(() => {
    if (!canViewPage) return;

    loadReport();
  }, [canViewPage, loadReport]);

  const visibleRows = useMemo(() => rows, [rows]);
  const firstRowKeyByEntryId = useMemo(() => {
    const map = new Map();
    (visibleRows || []).forEach((row) => {
      const entryId = String(row.entryId || '');
      if (!entryId || map.has(entryId)) return;
      map.set(entryId, `${entryId}:${row.lineItemId || row.productId || row.productName}:${row.sku || 'nosku'}`);
    });
    return map;
  }, [visibleRows]);

  useEffect(() => {
    if (!linkedEntryId || loading || visibleRows.length === 0) return;

    const rowKey = firstRowKeyByEntryId.get(String(linkedEntryId));
    const rowNode = rowKey ? rowRefs.current.get(rowKey) : null;
    if (!rowNode) return;

    window.setTimeout(() => {
      rowNode.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
    }, 80);
  }, [firstRowKeyByEntryId, linkedEntryId, loading, visibleRows.length]);

  const summary = useMemo(() => ({
    totalRows: meta.totalRows || visibleRows.length,
    totalEntries: meta.totalEntries || 0,
    inwardMemoCount: meta.inwardMemoCount || 0,
    outwardMemoCount: meta.outwardMemoCount || 0,
  }), [meta, visibleRows.length]);

  const exportExcel = () => {
    downloadExcelWorkbook({
      fileName: `daily-dairy-order-report-${fromDate}-to-${toDate}.xlsx`,
      sheetName: 'Dairy Orders',
      columns: [
        { header: 'Transaction Date', key: 'transactionDate', width: 16 },
        { header: 'Transaction Type', key: 'transactionType', width: 18 },
        { header: 'Store', key: 'storeName', width: 20 },
        { header: 'Memo Number', key: 'memoNumber', width: 18 },
        { header: 'Product', key: 'productName', width: 30 },
        { header: 'SKU', key: 'sku', width: 18 },
        { header: 'Quantity', key: 'quantity', width: 12 },
        { header: 'Entry Total Quantity', key: 'totalQuantity', width: 18 },
        { header: 'Received By', key: 'receivedBy', width: 18 },
        { header: 'Adjusted By', key: 'adjustedBy', width: 18 },
        { header: 'Created By', key: 'createdByName', width: 18 },
        { header: 'Created At', key: 'createdAt', width: 22 },
        { header: 'Last Edited', width: 42, value: (row) => formatEditInfo(row) },
      ],
      rows: visibleRows,
    });
  };

  if (!canViewPage) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">Daily Dairy Order Report</h1>
        <div className="card">
          <p className="text-gray-500">Access denied.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 border-b border-slate-200 pb-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-[24px] font-bold tracking-tight text-slate-900">Daily Dairy Order Report</h1>
          <p className="mt-1 max-w-3xl text-sm text-slate-600">
            Review inward and outward dairy transactions with product-level detail and memo tracking.
          </p>
        </div>
        <ExportButton onClick={exportExcel} />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-4">
        <div className="card relative overflow-hidden border border-slate-200 bg-[radial-gradient(circle_at_top_left,_rgba(148,163,184,0.18),_transparent_42%),linear-gradient(135deg,_#f8fafc_0%,_#ffffff_48%,_#f1f5f9_100%)]">
          <div className="pointer-events-none absolute inset-0 opacity-40" style={{ backgroundImage: 'radial-gradient(rgba(148,163,184,0.28) 0.7px, transparent 0.7px)', backgroundSize: '12px 12px' }} />
          <div className="relative flex items-center gap-3">
            <FileSpreadsheet className="h-6 w-6 text-slate-600" />
            <div>
              <p className="text-sm font-medium text-slate-700">Product Rows</p>
              <p className="text-2xl font-bold text-slate-900">{summary.totalRows}</p>
            </div>
          </div>
        </div>
        <div className="card border border-emerald-200 bg-emerald-50">
          <div className="flex items-center gap-3">
            <Store className="h-6 w-6 text-emerald-600" />
            <div>
              <p className="text-sm font-medium text-emerald-800">Entries</p>
              <p className="text-2xl font-bold text-emerald-700">{summary.totalEntries}</p>
            </div>
          </div>
        </div>
        <div className="card border border-sky-200 bg-sky-50">
          <div className="flex items-center gap-3">
            <Store className="h-6 w-6 text-sky-600" />
            <div>
              <p className="text-sm font-medium text-sky-800">Inward Memo</p>
              <p className="text-2xl font-bold text-sky-700">{summary.inwardMemoCount}</p>
            </div>
          </div>
        </div>
        <div className="card border border-rose-200 bg-rose-50">
          <div className="flex items-center gap-3">
            <Store className="h-6 w-6 text-rose-600" />
            <div>
              <p className="text-sm font-medium text-rose-800">Outward Memo</p>
              <p className="text-2xl font-bold text-rose-700">{summary.outwardMemoCount}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="flex flex-wrap items-end gap-3">
          <label className="relative block min-w-[200px] flex-[1.05_1_240px]">
            <span className="label">Search</span>
            <Search className="pointer-events-none absolute left-3 top-[2.55rem] h-4 w-4 text-gray-400" />
            <input
              className="input pl-10"
              placeholder="Memo, product, SKU, user..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </label>

          <FilterSelect
            label="Transaction"
            value={transactionType}
            options={transactionTypeOptions}
            onChange={setTransactionType}
            className="w-[150px]"
          />

          <div className="w-[125px]">
            <label className="label">Date From</label>
            <input className="input text-sm" type="date" value={fromDate} max={toDate} onChange={(event) => setFromDate(event.target.value)} />
          </div>

          <div className="w-[125px]">
            <label className="label">Date To</label>
            <input className="input text-sm" type="date" value={toDate} min={fromDate} max={todayKey()} onChange={(event) => setToDate(event.target.value)} />
          </div>
        </div>
      </div>

      <div className="card table-scroll-shell">
        <table className="min-w-[1710px] divide-y divide-slate-200 text-sm">
          <thead>
            <tr className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <th className="px-4 py-3 font-semibold">Date</th>
              <th className="px-4 py-3 font-semibold">Type</th>
              <th className="px-4 py-3 font-semibold">Store</th>
              <th className="px-4 py-3 font-semibold">Memo Number</th>
              <th className="px-4 py-3 font-semibold">Product</th>
              <th className="px-4 py-3 font-semibold">SKU</th>
              <th className="px-4 py-3 font-semibold text-right">Qty</th>
              <th className="px-4 py-3 font-semibold text-right">Entry Total</th>
              <th className="px-4 py-3 font-semibold">Received By</th>
              <th className="px-4 py-3 font-semibold">Adjusted By</th>
              <th className="px-4 py-3 font-semibold">Created By</th>
              <th className="px-4 py-3 font-semibold">Logged At</th>
              <th className="px-4 py-3 font-semibold">Last Edited</th>
              <th className="px-4 py-3 font-semibold text-center">Request to Edit</th>
              <th className="px-4 py-3 font-semibold text-center">Export</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {visibleRows.map((row) => {
              const rowKey = `${row.entryId}:${row.lineItemId || row.productId || row.productName}:${row.sku || 'nosku'}`;
              const isEntryPrimaryRow = firstRowKeyByEntryId.get(String(row.entryId || '')) === rowKey;
              const isLinkedEntryRow = linkedEntryId && String(row.entryId || '') === String(linkedEntryId);
              const locked = String(row.confirmationStatus || '').toLowerCase() === 'confirmed'
                && (Boolean(row.editControl?.confirmedLocked) || !row.editControl?.unlockedAt);
              const requested = Boolean(row.editControl?.unlockRequested);
              return (
              <tr
                key={`${row.entryId}-${row.lineItemId || row.productId || row.productName}-${row.sku || 'nosku'}`}
                ref={(node) => {
                  if (node) rowRefs.current.set(rowKey, node);
                  else rowRefs.current.delete(rowKey);
                }}
                className={`hover:bg-slate-50/70 ${
                  isLinkedEntryRow ? 'bg-amber-50 ring-2 ring-inset ring-amber-300' : ''
                }`}
              >
                <td className="px-4 py-3 text-slate-700">{formatDate(row.transactionDate)}</td>
                <td className="px-4 py-3">
                  <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${row.transactionType === 'inward' ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                    {row.transactionType}
                  </span>
                </td>
                <td className="px-4 py-3 text-slate-700">{row.storeName}</td>
                <td className="px-4 py-3 font-medium text-slate-900">{row.memoNumber}</td>
                <td className="px-4 py-3 text-slate-800">{row.productName}</td>
                <td className="px-4 py-3 font-mono text-slate-500">{row.sku || '-'}</td>
                <td className="px-4 py-3 text-right font-semibold text-slate-800">{row.quantity}</td>
                <td className="px-4 py-3 text-right text-slate-700">{row.totalQuantity}</td>
                <td className="px-4 py-3 text-slate-700">{row.receivedBy}</td>
                <td className="px-4 py-3 text-slate-700">{row.adjustedBy}</td>
                <td className="px-4 py-3 text-slate-700">{row.createdByName || '-'}</td>
                <td className="px-4 py-3 text-slate-500">{row.createdAt ? new Date(row.createdAt).toLocaleString() : '-'}</td>
                <td className="px-4 py-3 text-slate-600">
                  {row.lastEditedAt || row.createdAt ? (
                    <div className="min-w-[280px]">
                      <div className="font-medium text-slate-800">{new Date(row.lastEditedAt || row.createdAt).toLocaleString()}</div>
                      <div className="mt-0.5 text-xs font-semibold text-slate-500">{row.lastEditedByName || row.createdByName || '-'}</div>
                      <div className="mt-1 text-xs leading-5 text-slate-500">{row.lastEditSummary || 'Initial entry created'}</div>
                    </div>
                  ) : '-'}
                </td>
                <td className="px-4 py-3 text-center">
                  {!isEntryPrimaryRow ? (
                    <span className="text-xs text-slate-400">Same entry</span>
                  ) : locked && requested ? (
                    isAdmin() ? (
                      <div className="inline-flex flex-col items-center gap-1">
                        <div className="inline-flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => handleApproveEdit(row)}
                            className="inline-flex items-center gap-1.5 rounded-xl bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-indigo-500"
                          >
                            Approve
                          </button>
                          <button
                            type="button"
                            onClick={() => handleRejectEdit(row)}
                            className="inline-flex items-center gap-1.5 rounded-xl border border-rose-200 bg-white px-3 py-1.5 text-xs font-semibold text-rose-700 transition hover:bg-rose-50"
                          >
                            Reject
                          </button>
                        </div>
                        <span className="text-[11px] text-slate-500">
                          Requested by {row.editControl?.unlockRequestedByName || 'staff'}
                        </span>
                      </div>
                    ) : (
                      <div className="inline-flex flex-col items-center gap-1">
                        <span className="inline-flex rounded-full bg-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-700">Requested</span>
                        <span className="text-[11px] text-slate-500">
                          Requested by {row.editControl?.unlockRequestedByName || 'staff'}
                        </span>
                      </div>
                    )
                  ) : row.editControl?.unlockedAt ? (
                    <span className="inline-flex rounded-full  px-2.5 py-1 text-xs font-semibold text-emerald-700">
                      Approved on {new Date(row.editControl.unlockedAt).toLocaleString()}
                    </span>
                  ) : (
                    <span className="text-xs text-slate-400">-</span>
                  )}
                </td>
                <td className="px-4 py-3 text-center">
                  <ExportButton size="sm" onClick={() => exportSingleRow(row)} />
                </td>
              </tr>
            );})}
          </tbody>
        </table>

        {!loading && visibleRows.length === 0 ? (
          <div className="px-4 py-12 text-center text-sm text-slate-500">
            No dairy order records found for the selected filters.
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default DailyDairyOrderReport;
